import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { LanguageConstants } from "../constants/LanguageConstants";

/**
 * Bulk Excel preview body (Office Online embed). Render inside react-bootstrap `<Modal>`.
 * `data` must be a public HTTPS URL from `POST /quotes/quotes-xlsx-preview`.
 */
type ExcelSheetProps = {
  data?: string;
  fileName?: string;
  /** Fetching preview URL from API */
  apiLoading?: boolean;
  /** API or URL resolution failed */
  error?: string | null;
  onClose: () => void;
};

function ExcelSheet({ data, fileName, apiLoading = false, error = null, onClose }: ExcelSheetProps) {
  const [embedLoading, setEmbedLoading] = useState(false);

  const src =
    typeof data === "string" && data.trim().length > 0
      ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(data.trim())}`
      : "";

  useEffect(() => {
    if (src) {
      setEmbedLoading(true);
    } else {
      setEmbedLoading(false);
    }
  }, [src]);

  const showEmbedLoader = Boolean(src && !error && embedLoading);
  const showLoader = apiLoading || showEmbedLoader;

  return (
    <>
      <Modal.Header className="excel-preview-modal__header">
        <Modal.Title id="sheetLabel">{fileName}</Modal.Title>
        <button type="button" className="btn-close" onClick={onClose} aria-label={LanguageConstants?.ARIA_CLOSE ?? "Close"}>
          <i className="icon-close-square" aria-hidden />
        </button>
      </Modal.Header>
      <Modal.Body className="excel-preview-modal__body p-0">
        {showLoader ? (
          <div className="excel-preview-modal__loader" role="status" aria-live="polite" aria-busy="true">
            <div className="spinner-border text-primary excel-preview-modal__spinner" aria-hidden />
            <span className="visually-hidden">Loading</span>
          </div>
        ) : null}
        {error ? (
          <p className="excel-preview-modal__error mb-0">{error}</p>
        ) : null}
        {src && !error && !apiLoading ? (
          <iframe
            src={src}
            className="excel-preview-modal__iframe"
            frameBorder={0}
            title={LanguageConstants?.BULK_PREVIEW_UPLOADED_FILE ?? "Excel Preview"}
            onLoad={() => setEmbedLoading(false)}
          />
        ) : null}
      </Modal.Body>
    </>
  );
}

export default ExcelSheet;
